import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { artworkId } from "../lib/ids";
import type { frontend as fr } from "../client";

const PlaceholderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    className="w-7 h-7 opacity-30"
  >
    <path d="M12 2L12 8M12 16L12 22M2 12L8 12M16 12L22 12M4.93 4.93L9.17 9.17M14.83 14.83L19.07 19.07M4.93 19.07L9.17 14.83M14.83 9.17L19.07 4.93" />
  </svg>
);

const GalleryCard = ({ artwork }: { artwork: fr.Artwork }) => {
  const cover = artwork.Images[0];
  return (
    <Link
      to={`/work/${artworkId(artwork.ID)}`}
      className="group block focus:outline-none"
    >
      <div className="relative aspect-[4/5] bg-[var(--color-kj-placeholder)] border border-[var(--color-kj-border)] overflow-hidden">
        {cover ? (
          <img
            src={cover.URL}
            alt={artwork.Title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <PlaceholderIcon />
            <span className="mt-2 font-display italic text-[var(--color-kj-accent)] text-base">
              Coming Soon
            </span>
          </div>
        )}
      </div>
      <div className="pt-3">
        <p className="font-display text-lg text-[var(--color-kj-text)]">
          {artwork.Title}
        </p>
      </div>
    </Link>
  );
};

const HomePage = () => {
  const artworksQuery = useQuery({
    queryKey: ["artworks", "public"],
    queryFn: () => apiClient.frontend.ListArtworks({ IncludeSold: true }),
  });

  const artworks = artworksQuery.data?.Artworks ?? [];
  const hasArtworks = artworks.length > 0;

  return (
    <section className="max-w-6xl mx-auto px-6 md:px-8 py-16">
      <h2 className="font-display text-2xl uppercase-track text-center mb-12">
        Work
      </h2>

      {artworksQuery.isLoading && (
        <p className="text-center text-sm text-[var(--color-kj-text-light)]">
          Loading…
        </p>
      )}

      {artworksQuery.isError && (
        <p className="text-center text-sm text-[var(--color-kj-danger)]">
          Unable to load artwork at the moment.
        </p>
      )}

      {artworksQuery.isSuccess && !hasArtworks && (
        <p className="text-center text-sm text-[var(--color-kj-text-light)] italic font-display">
          New work coming soon.
        </p>
      )}

      {hasArtworks && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {artworks.map((a) => (
            <GalleryCard key={artworkId(a.ID)} artwork={a} />
          ))}
        </div>
      )}
    </section>
  );
};

export default HomePage;
